import * as React from "react";
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Alert,
    AlertColor,
    Typography,
} from "@mui/material";
import CclInfoTable, {
    CclInfoTableFormValue,
    CclInfoTableRowTemplate,
} from "../../../../../components/common/cclInfoTable/cclInfoTable";
import useLogAccessEvent from "../../../../../hooks/useLogAccessEvent";
import {
    useGetSessionSetUpByEventKeyQuery,
    usePutSessionSetUpMutation,
} from "../../../../../services/cclTokenedSessionApi";
import { SessionProgramData } from "../../../../../services/types/sessionDetailTypes";
import { AccessEventSessionDetails } from "../../../../../services/types/accessEventTypes";
import CclGenericAccordian from "../../../../../components/common/cclGenericAccordian";
import ComponentLoader from "../../../../../components/common/componentLoader";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { FeatureFlag } from "../../../../../featureFlags";
import { CclInfoTblSetupProductSaleTemplate } from "./cclInfoTbiSetupProductSaleTemplate";

interface ProductSaleSetupAccordionProps {
    esKey: number;
    readOnly?: boolean;
    showSnackbar: (message: string, severity: AlertColor) => void;
}

const toValidDate = (d: Date | null | undefined): Date | undefined => {
    if (d == null) return undefined;
    const date = d as Date;
    if (isNaN(date.getTime())) return undefined;
    // Use UTC components (the intended calendar date) to create a local midnight date,
    // so MUI day comparisons align with the calendar date the user sees.
    return new Date(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate(), 0, 0, 0, 0);
};

const addDay = (d: Date): Date => {
    const next = new Date(d);
    next.setDate(next.getDate() + 1);
    return next;
};

const ProductSaleSetupAccordion: React.FC<ProductSaleSetupAccordionProps> = (props) => {
    const [incomingSessionProgramData, setIncomingSessionProgramData] =
        React.useState<SessionProgramData | null>(null);
    const {
        data: sessionSetupData,
        isSuccess,
        isLoading,
    } = useGetSessionSetUpByEventKeyQuery(props.esKey.toString(), {
        skip: !props.esKey || props.esKey <= 0,
    });
    const [putSessionSetup, { isLoading: sessionSetupIsLoading }] = usePutSessionSetUpMutation();
    const [completing, setCompleting] = React.useState<boolean>(false);
    const { logEvent } = useLogAccessEvent();
    const features = React.useContext(FeatureFlag);

    const [finalTemplate, setFinalTemplate] = React.useState<CclInfoTableRowTemplate[]>([]);
    const [localSessionProgramData, setLocalSessionProgramData] =
        React.useState<SessionProgramData | null>(null);
    const [sessionValidationMsg, setSessionValidationMsg] = React.useState<string>("");
    //eslint-disable-next-line
    const [expanded, setExpanded] = React.useState<boolean>(true);

    React.useEffect(() => {
        if (sessionSetupData?.sessionProgramData != null) {
            var data = { ...sessionSetupData.sessionProgramData };
            if (data.hotelUrl != null) {
                data.hotelUrl = decodeURIComponent(data.hotelUrl);
            }
            setIncomingSessionProgramData(data);
        }
    }, [sessionSetupData]);

    React.useEffect(() => {
        if (incomingSessionProgramData == null) {
            setLocalSessionProgramData(null);
            setFinalTemplate([]);
            return;
        }

        let newLocalSessionProgramData: SessionProgramData = { ...incomingSessionProgramData };

        // make sure program dates are utc
        newLocalSessionProgramData.startDate =
            newLocalSessionProgramData.startDate != null
                ? new Date(newLocalSessionProgramData.startDate + "Z")
                : null;
        newLocalSessionProgramData.endDate =
            newLocalSessionProgramData.endDate != null
                ? new Date(newLocalSessionProgramData.endDate + "Z")
                : null;
        newLocalSessionProgramData.preworkInvitationDate = new Date(
            newLocalSessionProgramData.preworkInvitationDate + "Z",
        );
        newLocalSessionProgramData.surveyCompletionDate = new Date(
            newLocalSessionProgramData.surveyCompletionDate + "Z",
        );
        newLocalSessionProgramData.scoreDate = new Date(newLocalSessionProgramData.scoreDate + "Z");
        newLocalSessionProgramData.reminder1 = new Date(newLocalSessionProgramData.reminder1 + "Z");
        newLocalSessionProgramData.reminder2 = new Date(newLocalSessionProgramData.reminder2 + "Z");
        newLocalSessionProgramData.reminder3 = new Date(newLocalSessionProgramData.reminder3 + "Z");

        if (newLocalSessionProgramData.hotelUrl != null) {
            newLocalSessionProgramData.hotelUrl = decodeURI(newLocalSessionProgramData.hotelUrl);
        }

        setLocalSessionProgramData(newLocalSessionProgramData);

        var newTemplate = CclInfoTblSetupProductSaleTemplate;
        if (props.readOnly === true) {
            newTemplate.forEach((e: CclInfoTableRowTemplate) => {
                e.editable = false;
            });
        }

        setFinalTemplate(newTemplate);
    }, [incomingSessionProgramData, props.readOnly, features]);

    React.useEffect(() => {
        if (!localSessionProgramData) return;

        const today = new Date(new Date().toDateString());
        const inv = toValidDate(localSessionProgramData.preworkInvitationDate);
        const r1 = toValidDate(localSessionProgramData.reminder1);
        const r2 = toValidDate(localSessionProgramData.reminder2);
        const r3 = toValidDate(localSessionProgramData.reminder3);
        const completion = toValidDate(localSessionProgramData.surveyCompletionDate);
        const sessionDate = toValidDate(localSessionProgramData.startDate ?? undefined);
        const baseEditable =
            props.readOnly !== true && localSessionProgramData.hostingPlatformId !== 3;

        setFinalTemplate((prev) =>
            prev.map((row) => {
                const updated = { ...row, valueField: { ...row.valueField } };
                switch (row.id) {
                    case "preworkInvitationDate":
                        updated.valueField.minDate = today;
                        updated.valueField.maxDate = r1 ?? r2 ?? r3 ?? completion;
                        break;
                    case "reminder1":
                        updated.required = !!r2;
                        updated.valueField.minDate = inv ? addDay(inv) : today;
                        updated.valueField.maxDate = r2 ?? r3 ?? completion;
                        break;
                    case "reminder2":
                        updated.editable = baseEditable && !!r1;
                        updated.required = !!r3;
                        updated.valueField.minDate = r1 ? addDay(r1) : today;
                        updated.valueField.maxDate = r3 ?? completion;
                        break;
                    case "reminder3":
                        updated.editable = baseEditable && !!r2;
                        updated.valueField.minDate = r2 ? addDay(r2) : today;
                        updated.valueField.maxDate = completion;
                        break;
                    case "surveyCompletionDate": {
                        const completionMin = r3 ?? r2 ?? r1 ?? inv;
                        updated.valueField.minDate = completionMin ? addDay(completionMin) : today;
                        updated.valueField.maxDate = sessionDate;
                        break;
                    }
                    case "startDate":
                        updated.valueField.minDate = today;
                        updated.valueField.maxDate = undefined;
                        break;
                }
                return updated;
            }),
        );
    }, [localSessionProgramData, props.readOnly]);

    const validateSessionProgramData = (sessionData: SessionProgramData): boolean => {
        let msg = "";

        // Prepare date variables for validation
        const surveyCompletionDate =
            (sessionData.surveyCompletionDate as any) != null &&
            !isNaN(sessionData.surveyCompletionDate.getTime())
                ? new Date(sessionData.surveyCompletionDate.toDateString())
                : null;
        const preworkInvitationDate =
            (sessionData.preworkInvitationDate as any) != null &&
            !isNaN(sessionData.preworkInvitationDate.getTime())
                ? new Date(sessionData.preworkInvitationDate.toDateString())
                : null;
        const reminder1Date =
            sessionData.reminder1 != null && !isNaN(sessionData.reminder1.getTime())
                ? new Date(sessionData.reminder1.toDateString())
                : null;
        const reminder2Date =
            sessionData.reminder2 != null && !isNaN(sessionData.reminder2.getTime())
                ? new Date(sessionData.reminder2.toDateString())
                : null;
        const reminder3Date =
            sessionData.reminder3 != null && !isNaN(sessionData.reminder3.getTime())
                ? new Date(sessionData.reminder3.toDateString())
                : null;
        const endDate =
            sessionData.endDate != null && !isNaN(sessionData.endDate.getTime())
                ? new Date(sessionData.endDate.toDateString())
                : null;

        // 1. Assessment Completion is always required
        if (!surveyCompletionDate) {
            if (msg !== "") msg += ", ";
            msg += "Assessment Completion is required";
        }

        // 4. Invitation must be before Assessment Completion
        if (
            preworkInvitationDate &&
            surveyCompletionDate &&
            preworkInvitationDate >= surveyCompletionDate
        ) {
            if (msg !== "") msg += ", ";
            msg += "Invitation (Scheduled) must be before Assessment Completion";
        }

        // 5. Reminders: after Invitation (if set) and before Assessment Completion
        const reminders = [
            { date: reminder1Date, num: 1 },
            { date: reminder2Date, num: 2 },
            { date: reminder3Date, num: 3 },
        ];
        const setReminders = reminders.filter((r) => r.date !== null);

        if (setReminders.length > 0) {
            let hasReminderError = false;

            if (preworkInvitationDate) {
                for (const reminder of setReminders) {
                    if (
                        reminder.date &&
                        (reminder.date <= preworkInvitationDate ||
                            (surveyCompletionDate && reminder.date >= surveyCompletionDate))
                    ) {
                        hasReminderError = true;
                        break;
                    }
                }
            } else {
                for (const reminder of setReminders) {
                    if (
                        reminder.date &&
                        surveyCompletionDate &&
                        reminder.date >= surveyCompletionDate
                    ) {
                        hasReminderError = true;
                        break;
                    }
                }
            }

            if (hasReminderError) {
                if (msg !== "") msg += ", ";
                msg += preworkInvitationDate
                    ? "Reminders must be between Invitation (Scheduled) and Assessment Completion"
                    : "Reminders must be before Assessment Completion";
            }
        }

        // 6. Assessment Completion must be before Session End Date
        if (surveyCompletionDate && endDate && surveyCompletionDate >= endDate) {
            if (msg !== "") msg += ", ";
            msg += "Assessment Completion must be before Session Date";
        }

        setSessionValidationMsg(msg);
        return msg === "";
    };

    const saveSetupData = (setupData: SessionProgramData | null, isCompleteRequest: boolean) => {
        if (!setupData) {
            props.showSnackbar("Setup Update Failed", "error");
            setCompleting(false);
            return;
        }

        if (!validateSessionProgramData(setupData)) {
            props.showSnackbar("Setup Update Failed", "error");
            setCompleting(false);
            return;
        }

        putSessionSetup(setupData)
            .unwrap()
            .then((result) => {
                setCompleting(false);
                if (result.success) {
                    const evtData: AccessEventSessionDetails = {
                        sessionID: setupData.programId.toString() ?? "",
                        projectCode: setupData.programCode ?? "",
                    };

                    if (isCompleteRequest) {
                        logEvent("SessionSetupCompleted", evtData);
                        props.showSnackbar("Setup Completed", "success");
                    } else {
                        logEvent("SessionSetupSaved", evtData);
                        props.showSnackbar("Setup Updated", "success");
                    }
                } else {
                    if (isCompleteRequest) {
                        props.showSnackbar("Setup Completion Failed", "error");
                    } else {
                        props.showSnackbar("Setup Update Failed", "error");
                    }
                }
            })
            .catch(() => {
                setCompleting(false);
                if (setupData.statusTypeId === 1) {
                    props.showSnackbar("Setup Completion Failed", "error");
                } else {
                    props.showSnackbar("Setup Update Failed", "error");
                }
            });
    };

    const handleUpdate = (changes: CclInfoTableFormValue[]) => {
        if (changes.length === 0) return;
        if (localSessionProgramData === null) return;
        let newLocal: SessionProgramData = { ...localSessionProgramData };
        changes.forEach((element: CclInfoTableFormValue) => {
            if (
                element.name === "preworkInvitationDate" ||
                element.name === "surveyCompletionDate" ||
                element.name === "scoreDate"
            ) {
                if (element.value === "" || element.value === null) {
                    (newLocal as any)[element.name] = null;
                } else {
                    let temp = new Date(element.value);
                    let dt = new Date(
                        Date.UTC(temp.getFullYear(), temp.getMonth(), temp.getDate(), 0, 0, 0, 0),
                    );
                    newLocal[element.name] = dt;
                }
            } else if (
                element.name === "reminder1" ||
                element.name === "reminder2" ||
                element.name === "reminder3"
            ) {
                if (element.value === "" || element.value === null) {
                    newLocal[element.name] = null;
                } else {
                    let temp = new Date(element.value);
                    let dt = new Date(
                        Date.UTC(temp.getFullYear(), temp.getMonth(), temp.getDate(), 0, 0, 0, 0),
                    );
                    newLocal[element.name] = dt;
                }
            } else if (element.name === "hotelUrl") {
                newLocal[element.name] =
                    element.value === "" ? null : encodeURIComponent(element.value);
            } else if (element.name === "startDate") {
                let temp = new Date(element.value);
                let dt = new Date(
                    Date.UTC(temp.getFullYear(), temp.getMonth(), temp.getDate(), 0, 0, 0, 0),
                );
                newLocal[element.name] = dt;
                newLocal["endDate"] = dt; // for product sales keep start and end date the same (end date is not visible on form)
            } else {
                const key: keyof SessionProgramData = element.name as any; // Explicitly define the type of 'key'
                newLocal[key] = element.value as never;
            }
        });

        validateSessionProgramData(newLocal);
        setLocalSessionProgramData(newLocal);
    };

    if (isLoading) {
        return (
            <CclGenericAccordian headingText="Setup" defaultExpanded={true} sx={{ h: "100%" }}>
                <ComponentLoader msg={"Loading Session Setup Info"} />
            </CclGenericAccordian>
        );
    }

    if (isSuccess && incomingSessionProgramData != null) {
        return (
            <Accordion
                sx={expanded ? { height: "100%" } : {}}
                expanded={expanded}
                onChange={() => setExpanded(!expanded)}
            >
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography sx={{ width: "100%", marginLeft: "15px", fontWeight: "bolder" }}>
                        Setup
                    </Typography>
                </AccordionSummary>
                <AccordionDetails>
                    {sessionSetupData.sessionProgramData?.statusTypeId !== 1 ? (
                        <Alert severity="info" sx={{ mb: 3 }}>
                            Session status may take up to 5 minutes to update across all screens
                            after Completing Setup
                        </Alert>
                    ) : null}
                    <CclInfoTable
                        onSave={() => saveSetupData(localSessionProgramData, false)}
                        template={finalTemplate}
                        data={incomingSessionProgramData}
                        onChange={handleUpdate}
                        disableSave={sessionValidationMsg !== "" || completing}
                        changeAlertSeverity={sessionValidationMsg !== "" ? "error" : undefined}
                        changeAlertMessage={
                            sessionValidationMsg !== "" ? sessionValidationMsg : undefined
                        }
                        saving={sessionSetupIsLoading && !completing}
                    />
                </AccordionDetails>
            </Accordion>
        );
    }

    // error case
    return (
        <CclGenericAccordian headingText="Setup" defaultExpanded={true} sx={{ height: "100%" }}>
            <Alert severity="error">
                There was an error retrieving Setup information for this session
            </Alert>
        </CclGenericAccordian>
    );
};

export default ProductSaleSetupAccordion;
